import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import TeamsTab from "./management/TeamsTab";
import UserManagement from "./settings/UserManagement";
import CompanyManagement from "./settings/CompanyManagement";
import UserCompensation from "./settings/UserCompensation";

// ============================================================================
// MANAGEMENT
// ----------------------------------------------------------------------------
// People and companies, separated from Settings.
//
// Settings had grown to eight tabs covering two unrelated jobs: configuring
// the account (payment terms, subscription, app preferences — set once and
// rarely touched) and running the organisation (who works here, which company
// they belong to, which team they are on, what they are paid). The second kind
// is weekly work, and burying it behind a gear icon made it feel like
// configuration nobody should touch.
//
// Audit Log deliberately stays in Settings. It is a record of the account, not
// a thing you manage, and it is read in the same frame of mind as the other
// account-level screens.
//
// These tabs render the exact same components they did under Settings — nothing
// about user, company or compensation behaviour changes here, only where it is
// reached from. Each component keeps its own role guard, so a tab shown to
// somebody who may not act on it still refuses the write.
// ============================================================================

const rolesOf = (profile) => {
  if (!profile) return [];
  if (Array.isArray(profile.role)) return profile.role.filter(Boolean);
  return profile.role ? [profile.role] : [];
};

// Which tabs a role has any use for. A tab the viewer cannot act on at all is
// hidden rather than shown and then refused — "Access Denied" after a click
// tells somebody nothing except that the app looks broken.
//
// Companies and Teams stay visible to a Main Admin on purpose: they answer
// questions asked daily ("who is in my company", "who dispatches this driver")
// without going through a Super Admin. Companies is read-only for them; on
// Teams they can set which dispatcher runs each driver on their own team.
const TABS = [
  { id: "teams",   label: "Teams",             roles: ["Super Admin", "Main Admin", "Admin"] },
  { id: "user",    label: "Users",             roles: ["Super Admin", "Main Admin", "Admin"] },
  { id: "company", label: "Companies",         roles: ["Super Admin", "Main Admin", "Admin"] },
  { id: "comp",    label: "User Compensation", roles: ["Super Admin", "Admin"] },
];

export default function Management({ userProfile }) {
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");

  const visibleTabs = useMemo(() => {
    const roles = rolesOf(userProfile);
    // No role resolved yet (the profile is still loading) — show everything
    // rather than flashing an empty page, since each component guards itself.
    if (roles.length === 0) return TABS;
    return TABS.filter(t => t.roles.some(r => roles.includes(r)));
  }, [userProfile]);

  const [activeTab, setActiveTab] = useState(visibleTabs[0]?.id || "teams");

  // Deep links: /management?tab=user. Settings already worked this way, and the
  // tabs that moved here kept their ids, so links handed out before this split
  // still land on the right screen.
  //
  // Keyed on the tab string rather than the whole profile: a profile snapshot
  // arriving later must not drag somebody back to the tab their URL named after
  // they had already clicked elsewhere. A tab they may not see is corrected by
  // the effect below.
  useEffect(() => {
    if (requestedTab && TABS.some(t => t.id === requestedTab)) {
      setActiveTab(requestedTab);
    }
  }, [requestedTab]);

  // A role change (or the profile arriving) can take away the open tab.
  useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.some(t => t.id === activeTab)) {
      setActiveTab(visibleTabs[0].id);
    }
  }, [visibleTabs, activeTab]);

  // A dispatcher, driver or accountant has no tab here. The sidebar link is
  // already hidden from them, so this only shows for somebody who typed the
  // address or followed an old link — and one honest sentence beats four tabs
  // that each refuse them.
  if (visibleTabs.length === 0) {
    return (
      <div className="max-w-full mx-auto py-4 px-2 sm:px-6 lg:px-8">
        <h1 className="text-2xl sm:text-3xl font-bold mb-4 text-gray-800">Management</h1>
        <div className="bg-white rounded-lg shadow-md p-6">
          <p className="text-sm text-gray-600">
            Management covers users, companies and teams, and is limited to
            administrators. Ask your Super Admin if you need something changed here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-full mx-auto py-4 px-2 sm:px-6 lg:px-8">
      <h1 className="text-2xl sm:text-3xl font-bold mb-1 text-gray-800">Management</h1>
      <p className="text-sm text-gray-500 mb-6">
        People, companies and teams. Account configuration lives in Settings.
      </p>

      <div className="bg-white rounded-lg shadow-md">
        {/* Tab Buttons */}
        <div className="flex flex-wrap border-b border-gray-200 bg-gray-50 rounded-t-lg">
          {visibleTabs.map((tab, index) => (
            <button
              key={tab.id}
              className={`px-3 sm:px-6 py-3 font-medium text-sm sm:text-base focus:outline-none whitespace-nowrap
                ${
                  activeTab === tab.id
                    ? "border-b-2 border-blue-500 text-blue-600 bg-white"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                }
                ${index === 0 ? 'rounded-tl-lg' : ''}
              `}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="p-4 sm:p-6">
          {activeTab === "teams" && <TeamsTab loggedInUser={userProfile} />}
          {activeTab === "user" && <UserManagement />}
          {activeTab === "company" && <CompanyManagement />}
          {activeTab === "comp" && <UserCompensation />}
        </div>
      </div>
    </div>
  );
}
